/**
 * Sprint QA Projects - Project List View
 * Shows all accessible projects with QA stats in a card grid layout
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import useSuitesProjectsLanguage from './useSuitesProjectsLanguage';
import {
  FolderOpen,
  Plus,
  Search,
  FileText,
  Calendar,
  TestTube,
  ArrowRight,
  AlertCircle,
  Loader2,
  RefreshCw,
  Settings
} from 'lucide-react';

const getBackendURL = () => {
  return process.env.REACT_APP_BACKEND_URL || '';
};

export default function SprintQAProjects() {
  const { t, showError } = useSuitesProjectsLanguage();
  const BACKEND_URL = getBackendURL();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create project state
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  
  const createProject = async () => {
    if (!newProjectName.trim()) return;
    
    try {
      setCreatingProject(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/projects`, {
        name: newProjectName,
        description: newProjectDescription,
        icon: '📁',
        color: '#6366f1'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Navigate to the new project
      if (response.data?.slug) {
        window.history.pushState({}, '', `/sprint-qa/project/${response.data.slug}`);
        window.dispatchEvent(new CustomEvent('sprintQANavigate', { detail: `/sprint-qa/project/${response.data.slug}` }));
      } else {
        // Refresh the list if slug not returned
        fetchProjects();
        setShowCreateProject(false);
      }
      
      setNewProjectName('');
      setNewProjectDescription('');
    } catch (err) {
      console.error('Failed to create project:', err);
      const detail = err.response?.data?.detail;
      const projectLimit = typeof detail === 'string'
        ? detail.match(/^Project limit reached\. Your organization's plan allows (\d+) project\(s\)\. Please ask your administrator to upgrade\.$/)
        : null;
      showError(projectLimit ? t('Project limit reached. Your organization allows {count} projects. Ask your administrator to upgrade.', { count: projectLimit[1] })
        : detail === 'Project with this name already exists'
          ? t(detail)
          : detail || t('Failed to create project'));
    } finally {
      setCreatingProject(false);
    }
  };

  const handleProjectClick = (project) => {
    if (project.slug) {
      // Update URL
      window.history.pushState({}, '', `/sprint-qa/project/${project.slug}`);
      // Dispatch custom event to trigger re-render with the new path
      window.dispatchEvent(new CustomEvent('sprintQANavigate', { detail: `/sprint-qa/project/${project.slug}` }));
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separate legacy from regular projects
  const legacyProject = filteredProjects.find(p => p.is_legacy);
  const regularProjects = filteredProjects.filter(p => !p.is_legacy);

  if (loading) {
    return (
      <div className="h-[calc(100vh-180px)] flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">{t('Loading projects...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex-shrink-0">
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <FolderOpen className="w-6 h-6 text-indigo-600" />
            <h1 className="text-xl font-semibold text-gray-900">{t('Sprint QA Projects')}</h1>
            <Badge variant="secondary">{t('{count} Projects', { count: projects.length })}</Badge>
          </div>
          <div className="flex min-w-0 w-full flex-1 items-center gap-3 sm:w-auto sm:flex-none">
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder={t('Search projects...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 sm:w-64"
                data-testid="project-search"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={fetchProjects} aria-label={t('Refresh projects')} title={t('Refresh projects')}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Legacy Data Banner */}
        {legacyProject && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <h3 className="font-medium text-amber-900">{t('Unassigned QA Items')}</h3>
                  <p className="text-sm text-amber-700">
                    {t('You have {requirements} requirements and {sprints} sprints not assigned to any project.', {
                      requirements: legacyProject.requirement_count,
                      sprints: legacyProject.sprint_count
                    })}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={() => handleProjectClick(legacyProject)}
                >
                  {t('View Unassigned')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Project Grid */}
        {regularProjects.length === 0 && !legacyProject ? (
          <Card className="p-4 sm:p-8 lg:p-12 text-center max-w-lg mx-auto">
            <FolderOpen className="w-16 h-16 text-indigo-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('Welcome to Sprint QA')}</h3>
            <p className="text-sm text-gray-500 mb-6">
              {t('Create your first project to start managing sprints, requirements, and test cases.')}
            </p>
            
            {showCreateProject ? (
              <div className="text-left space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Project Name *')}</label>
                  <Input
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder={t('e.g., Mobile App, Web Platform, API Services')}
                    data-testid="new-project-name"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Description (optional)')}</label>
                  <Input
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    placeholder={t('Brief description of this project')}
                    data-testid="new-project-description"
                  />
                </div>
                <div className="flex flex-wrap gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => setShowCreateProject(false)} disabled={creatingProject}>
                    {t('Cancel')}
                  </Button>
                  <Button onClick={createProject} disabled={!newProjectName.trim() || creatingProject}>
                    {creatingProject ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('Creating...')}
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        {t('Create Project')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setShowCreateProject(true)} size="lg" className="w-full sm:w-auto max-w-full whitespace-normal h-auto min-h-10 py-2">
                <Plus className="w-4 h-4 mr-2" /> {t('Create Your First Project')}
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {regularProjects.map(project => (
              <Card 
                key={project.id} 
                className="cursor-pointer transition-all hover:shadow-lg hover:border-indigo-300 group"
                onClick={() => handleProjectClick(project)}
                data-testid={`project-card-${project.slug}`}
              >
                <div className="p-5">
                  {/* Project Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${project.color}20` }}
                    >
                      {project.icon || '📁'}
                    </div>
                    <Badge 
                      variant="outline" 
                      className="text-xs font-mono"
                      style={{ borderColor: project.color, color: project.color }}
                    >
                      {project.prefix}
                    </Badge>
                  </div>

                  {/* Project Name & Description */}
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                      {project.description}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 pt-3 border-t">
                    <div className="flex items-center gap-1" title={t('Sprints')}>
                      <Calendar className="w-4 h-4" />
                      <span>{project.sprint_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1" title={t('Requirements')}>
                      <FileText className="w-4 h-4" />
                      <span>{project.requirement_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1" title={t('Test Cases')}>
                      <TestTube className="w-4 h-4" />
                      <span>{project.test_case_count || 0}</span>
                    </div>
                  </div>

                  {/* Hover Action */}
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="outline" size="sm" className="w-full">
                      {t('Open Project')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
